import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { AppConfig } from '../../../Config';
import './LoginPage.css';

type GoogleButtonText = 'signin_with' | 'signup_with' | 'continue_with' | 'signin';

interface GoogleCredentialResponse {
  credential?: string;
}

interface GoogleAccountsIdApi {
  initialize: (config: {
    client_id: string;
    callback: (response: GoogleCredentialResponse) => void;
    auto_select?: boolean;
    cancel_on_tap_outside?: boolean;
    ux_mode?: 'popup' | 'redirect';
  }) => void;
  renderButton: (
    parent: HTMLElement,
    options: {
      theme?: 'outline' | 'filled_blue' | 'filled_black';
      size?: 'large' | 'medium' | 'small';
      text?: GoogleButtonText;
      shape?: 'rectangular' | 'pill' | 'circle' | 'square';
      logo_alignment?: 'left' | 'center';
      type?: 'standard' | 'icon';
      width?: number;
    }
  ) => void;
  cancel: () => void;
}

declare global {
  interface Window {
    google?: {
      accounts: {
        id: GoogleAccountsIdApi;
      };
    };
  }
}

interface GoogleIdentityButtonProps {
  disabled?: boolean;
  text?: GoogleButtonText;
  onCredential: (idToken: string) => void;
  onError: (message: string) => void;
}

const GOOGLE_IDENTITY_SCRIPT_ID = 'readerly-google-identity-script';
let googleIdentityScriptPromise: Promise<void> | null = null;

const loadGoogleIdentityScript = () => {
  if (window.google?.accounts?.id) {
    return Promise.resolve();
  }

  if (googleIdentityScriptPromise) {
    return googleIdentityScriptPromise;
  }

  googleIdentityScriptPromise = new Promise<void>((resolve, reject) => {
    const existingScript = document.getElementById(GOOGLE_IDENTITY_SCRIPT_ID) as HTMLScriptElement | null;
    if (existingScript) {
      existingScript.addEventListener('load', () => resolve(), { once: true });
      existingScript.addEventListener('error', () => reject(new Error('Google Identity script failed to load')), {
        once: true,
      });
      return;
    }

    const script = document.createElement('script');
    script.id = GOOGLE_IDENTITY_SCRIPT_ID;
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Google Identity script failed to load'));
    document.head.appendChild(script);
  });

  return googleIdentityScriptPromise;
};

const GoogleIdentityButton: React.FC<GoogleIdentityButtonProps> = ({
  disabled,
  text = 'signin_with',
  onCredential,
  onError,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const onCredentialRef = useRef(onCredential);
  const onErrorRef = useRef(onError);
  const [ready, setReady] = useState(false);
  const [buttonWidth, setButtonWidth] = useState<number | null>(null);

  useEffect(() => {
    onCredentialRef.current = onCredential;
    onErrorRef.current = onError;
  }, [onCredential, onError]);

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    let frameId = 0;
    const measure = () => {
      window.cancelAnimationFrame(frameId);
      frameId = window.requestAnimationFrame(() => {
        const measuredWidth = Math.floor(container.getBoundingClientRect().width);
        if (measuredWidth > 0) {
          setButtonWidth(Math.min(400, Math.max(200, measuredWidth)));
        }
      });
    };

    measure();
    window.addEventListener('resize', measure);

    let observer: ResizeObserver | null = null;
    if ('ResizeObserver' in window) {
      observer = new ResizeObserver(measure);
      observer.observe(container);
    }

    return () => {
      window.cancelAnimationFrame(frameId);
      window.removeEventListener('resize', measure);
      observer?.disconnect();
    };
  }, []);

  useEffect(() => {
    if (!AppConfig.GoogleLoginClientId) {
      return;
    }

    let mounted = true;
    loadGoogleIdentityScript()
      .then(() => {
        if (mounted) {
          setReady(true);
        }
      })
      .catch(() => {
        if (mounted) {
          onErrorRef.current('Не вдалося завантажити Google вхід. Спробуйте оновити сторінку.');
        }
      });

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (!ready || disabled || !buttonWidth || !containerRef.current || !window.google?.accounts?.id) {
      return;
    }

    const container = containerRef.current;
    const buttonText = buttonWidth < 260 ? 'signin' : text;

    container.innerHTML = '';
    window.google.accounts.id.initialize({
      client_id: AppConfig.GoogleLoginClientId,
      callback: (response) => {
        if (response.credential) {
          onCredentialRef.current(response.credential);
          return;
        }

        onErrorRef.current('Google не повернув підтвердження входу. Спробуйте ще раз.');
      },
      auto_select: false,
      cancel_on_tap_outside: true,
      ux_mode: 'popup',
    });

    window.google.accounts.id.renderButton(container, {
      theme: 'outline',
      size: 'large',
      text: buttonText,
      shape: 'pill',
      logo_alignment: 'left',
      width: buttonWidth,
    });

    return () => {
      window.google?.accounts?.id.cancel();
    };
  }, [buttonWidth, disabled, ready, text]);

  if (!AppConfig.GoogleLoginClientId) {
    return null;
  }

  return (
    <div
      className={disabled ? 'google-identity-button google-identity-button--disabled' : 'google-identity-button'}
      aria-disabled={disabled}
    >
      <div className="google-identity-button__surface" ref={containerRef} />
    </div>
  );
};

export default GoogleIdentityButton;
